/**
 * We don't use Matrix objects, rather arrays of numbers that we modify with static methods. I
 * haven't confirmed this but I suspect it is easier on the GC.
 */
export class Matrix {
  constructor() {
    throw Error();
  }

  static identity() {
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }

  static isIdentity(m: number[]) {
    return this.identity().every((value, i) => value == m[i]);
  }

  static translation(dx: number, dy: number): number[] {
    const m = [1, 0, dx, 0, 1, dy, 0, 0, 1];
    return m;
  }

  static scale(sx: number, sy: number): number[] {
    const m = [sx, 0, 0, 0, sy, 0, 0, 0, 1];
    return m;
  }

  static rotation(rad: number): number[] {
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const m = [cos, -sin, 0, sin, cos, 0, 0, 0, 1];
    return m;
  }

  static transformPoint(m: number[], x: number, y: number): number[] {
    // m * [x,y,1]
    return [m[0] * x + m[1] * y + m[2], m[3] * x + m[4] * y + m[5]];
  }

  static affinize(m: number[]): number[] {
    // If there are non-affine components, attempt to clear them out
    if (m[8] != 1 && m[8] != 0) return [m[0] / m[8], m[1], m[2], m[3], m[4] / m[8], m[5], 0, 0, 1];

    return m;
  }

  static determinant(m: number[]): number {
    return (
      m[0] * (m[4] * m[8] - m[5] * m[7]) -
      m[1] * (m[3] * m[8] - m[5] * m[6]) +
      m[2] * (m[3] * m[7] - m[4] * m[6])
    );
  }

  static invert(m: number[]): number[] {
    // Unrolled matrix inverse

    // http://www.wolframalpha.com/input/?i={{m0%2Cm1%2Cm2}%2C{m3%2Cm4%2Cm5}%2C{m6%2Cm7%2Cm8}}^-1

    /* 1/(m0 m4 m8-m0 m5 m7-m1 m3 m8+m1 m5 m6+m2 m3 m7-m2 m4 m6)(m4 m8-m5 m7 | m2 m7-m1 m8 | m1 m5-m2 m4
     m5 m6-m3 m8 | m0 m8-m2 m6 | m2 m3-m0 m5
     m3 m7-m4 m6 | m1 m6-m0 m7 | m0 m4-m1 m3) */

    const det =
      m[0] * (m[4] * m[8] - m[5] * m[7]) -
      m[1] * (m[3] * m[8] - m[5] * m[6]) +
      m[2] * (m[3] * m[7] - m[4] * m[6]);

    return [
      (m[4] * m[8] - m[5] * m[7]) / det,
      (m[2] * m[7] - m[1] * m[8]) / det,
      (m[1] * m[5] - m[2] * m[4]) / det,

      (m[5] * m[6] - m[3] * m[8]) / det,
      (m[0] * m[8] - m[2] * m[6]) / det,
      (m[2] * m[3] - m[0] * m[5]) / det,

      (m[3] * m[7] - m[4] * m[6]) / det,
      (m[1] * m[6] - m[0] * m[7]) / det,
      (m[0] * m[4] - m[1] * m[3]) / det,
    ];
  }

  static setMatrix(m: number[], c2d: CanvasRenderingContext2D) {
    c2d.setTransform(m[0], m[3], m[1], m[4], m[2], m[5]);
  }

  static appendMatrix(m: number[], c2d: CanvasRenderingContext2D) {
    // var b = (<any>c2d).currentTransform;
    c2d.transform(m[0], m[3], m[1], m[4], m[2], m[5]);
    // var a = (<any>c2d).currentTransform;
    // console.log(b, [m[0], m[3], m[1], m[4], m[2], m[5]], a);
  }

  static setCssMatrix(m: number[], style: CSSStyleDeclaration) {
    style.transform = this.getCssMatrix(m);
  }

  static getCssMatrix(m: number[]) {
    return `matrix(${m[0]},${m[3]},${m[1]},${m[4]},${m[2]},${m[5]})`;
  }

  static toDOMMatrix(m: number[]): DOMMatrix {
    return new DOMMatrix(this.getCssMatrix(m));
  }

  /**
   * Multiplies matrix 'm' by a matrix consisting of a translation only.
   */
  static postOffsetMatrix(m: number[], x: number, y: number): number[] {
    // TODO(mmastrac): Expand this out
    return this.multiply(m, [1, 0, x, 0, 1, y, 0, 0, 1]);
  }

  /**
   * Multiplies matrix 'm' by a matrix consisting of a translation only.
   */
  static preOffsetMatrix(m: number[], x: number, y: number): number[] {
    // TODO(mmastrac): Expand this out
    return this.multiply([1, 0, x, 0, 1, y, 0, 0, 1], m);
  }

  static multiply(m1: number[], m2: number[]) {
    // TODO(mmastrac): Unroll me
    const tmp = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        for (let k = 0; k < 3; k++) {
          tmp[j * 3 + i] += m1[k * 3 + i] * m2[j * 3 + k];
        }
      }
    }
    return tmp;
  }
}
